
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle2, MessageSquare, Send } from 'lucide-react';

interface Comment {
  id: string;
  comment: string;
  created_at: string;
  created_by: string;
  is_resolved: boolean;
  resolved_at?: string;
  resolved_by?: string;
  parent_comment_id?: string;
  user?: {
    full_name?: string;
    email?: string;
  };
  resolver?: {
    full_name?: string;
  };
}

interface CommentsPanelProps {
  documentId: string;
}

export function CommentsPanel({ documentId }: CommentsPanelProps) {
  const [newComment, setNewComment] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['documentComments', documentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('document_comments')
        .select(`
          *,
          user:created_by (
            full_name,
            email
          ),
          resolver:resolved_by (
            full_name
          )
        `)
        .eq('document_id', documentId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Comment[];
    },
    enabled: !!documentId,
  });

  const addCommentMutation = useMutation({
    mutationFn: async (comment: string) => {
      const { data, error } = await supabase
        .from('document_comments')
        .insert({
          document_id: documentId,
          comment,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['documentComments', documentId]});
      setNewComment('');
      toast({
        title: "Comment Added",
        description: "Your comment has been added to the document",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to add comment: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  });

  const resolveCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      const { data, error } = await supabase
        .from('document_comments')
        .update({
          is_resolved: true,
          resolved_at: new Date().toISOString(),
          resolved_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .eq('id', commentId)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['documentComments', documentId]});
      toast({
        title: "Comment Resolved",
        description: "The comment has been marked as resolved",
      });
    }
  });

  const handleSubmitComment = () => {
    if (newComment.trim()) {
      addCommentMutation.mutate(newComment);
    }
  };

  const handleResolveComment = (commentId: string) => {
    resolveCommentMutation.mutate(commentId);
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading comments...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <MessageSquare className="h-5 w-5 text-muted-foreground" />
        <h3 className="text-lg font-medium">Comments</h3>
        <div className="ml-2 bg-primary/10 text-primary text-xs rounded-full px-2 py-0.5">
          {comments.length}
        </div>
      </div>

      <div className="border rounded-md p-4 bg-card">
        <Textarea
          placeholder="Add a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          className="min-h-[100px] mb-2"
        />
        <div className="flex justify-end">
          <Button 
            size="sm" 
            onClick={handleSubmitComment}
            disabled={!newComment.trim() || addCommentMutation.isPending}
          >
            {addCommentMutation.isPending ? (
              "Submitting..."
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Add Comment
              </>
            )}
          </Button>
        </div>
      </div>

      {comments.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No comments yet. Be the first to comment on this document.
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div 
              key={comment.id} 
              className={`border rounded-lg p-4 ${comment.is_resolved ? 'bg-muted/30' : 'bg-card'}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>
                      {comment.user?.full_name?.charAt(0) || comment.user?.email?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium">{comment.user?.full_name || comment.user?.email || 'Unknown user'}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(comment.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                {!comment.is_resolved && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleResolveComment(comment.id)}
                    disabled={resolveCommentMutation.isPending}
                  >
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    Resolve
                  </Button>
                )}
              </div>
              <p className="py-2 whitespace-pre-wrap">{comment.comment}</p>
              {comment.is_resolved && (
                <div className="mt-2 text-xs border-t pt-2 text-muted-foreground">
                  Resolved by {comment.resolver?.full_name || 'a team member'} on {new Date(comment.resolved_at || '').toLocaleString()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
